import { useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from 'react-router-dom';

export const useFrontendMetrics = () => {
  const location = useLocation();

  useEffect(() => {
    let startTime = Date.now();
    let pageViews = 0;
    let lastRouteChangeTime = performance.now();

    // Coletar informações do navegador
    const getBrowserInfo = () => {
      const ua = navigator.userAgent;
      return {
        userAgent: ua,
        browser: ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i)?.[1] || '',
        browserVersion: ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i)?.[2] || '',
        platform: navigator.platform,
        language: navigator.language,
        cookiesEnabled: navigator.cookieEnabled,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`
      };
    };

    // Coletar métricas de recursos
    const getResourceTiming = () => {
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      return resources.map(resource => ({
        name: resource.name,
        duration: resource.duration,
        type: resource.initiatorType,
        size: resource.transferSize,
        startTime: resource.startTime
      }));
    };

    // Monitorar interações do usuário
    const trackUserInteraction = (event: MouseEvent | KeyboardEvent) => {
      const interaction = {
        type: event.type,
        timestamp: Date.now(),
        target: (event.target as HTMLElement)?.tagName || 'unknown',
        path: location.pathname
      };

      userInteractions.push(interaction);
    };

    let userInteractions: Array<{
      type: string;
      timestamp: number;
      target: string;
      path: string;
    }> = [];

    const collectMetrics = async () => {
      try {
        const perfEntries = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const paintEntries = performance.getEntriesByType('paint');

        const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint')?.startTime;
        const lcp = performance.getEntriesByType('largest-contentful-paint').pop()?.startTime;
        const tti = perfEntries.domInteractive;

        // Calcular duração da sessão
        const sessionDuration = Date.now() - startTime;

        // Calcular tempo de mudança de rota
        const routeChangeTime = performance.now() - lastRouteChangeTime;
        lastRouteChangeTime = performance.now();

        const { error, data } = await supabase.from('frontend_metrics').insert({
          page_url: window.location.pathname,
          fcp_time: Math.round(fcp || 0),
          lcp_time: Math.round(lcp || 0),
          tti_time: Math.round(tti || 0),
          js_errors: 0,
          network_errors: 0,
          page_views: pageViews,
          session_duration_ms: sessionDuration,
          route_change_time: Math.round(routeChangeTime),
          browser_info: getBrowserInfo(),
          user_interactions: userInteractions.slice(-50), // Manter apenas as últimas 50 interações
          resource_timing: getResourceTiming()
        }).select().single();

        if (error) throw error;

        // Limpar array de interações após salvar
        userInteractions = [];

        console.log('Frontend metrics collected:', data);
      } catch (error) {
        console.error('Error collecting frontend metrics:', error);
      }
    };

    // Incrementar visualizações de página
    pageViews++;

    // Coletar métricas após o carregamento da página
    window.addEventListener('load', collectMetrics);

    // Monitorar interações do usuário
    window.addEventListener('click', trackUserInteraction);
    window.addEventListener('keypress', trackUserInteraction);

    // Monitorar erros JavaScript
    const originalOnError = window.onerror;
    window.onerror = async function(message, source, lineno, colno, error) {
      try {
        const { data, error: queryError } = await supabase
          .from('frontend_metrics')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (data && !queryError) {
          await supabase
            .from('frontend_metrics')
            .update({ js_errors: (data.js_errors || 0) + 1 })
            .eq('id', data.id);
        }
      } catch (err) {
        console.error('Error updating JS errors count:', err);
      }

      if (originalOnError) {
        return originalOnError.apply(this, arguments as any);
      }
      return false;
    };

    // Monitorar erros de rede
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      try {
        const response = await originalFetch.apply(this, args);
        if (!response.ok) {
          const { data } = await supabase
            .from('frontend_metrics')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (data) {
            await supabase
              .from('frontend_metrics')
              .update({ network_errors: (data.network_errors || 0) + 1 })
              .eq('id', data.id);
          }
        }
        return response;
      } catch (error) {
        const { data } = await supabase
          .from('frontend_metrics')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (data) {
          await supabase
            .from('frontend_metrics')
            .update({ network_errors: (data.network_errors || 0) + 1 })
            .eq('id', data.id);
        }
        throw error;
      }
    };

    return () => {
      window.removeEventListener('load', collectMetrics);
      window.removeEventListener('click', trackUserInteraction);
      window.removeEventListener('keypress', trackUserInteraction);
      window.onerror = originalOnError;
      window.fetch = originalFetch;
    };
  }, [location]);
};